import type {
  CompleteUploadResponseDto,
  PresignRequestDtoMediaTypeEnum,
  PresignResponseDto,
} from '@nomiss/sdk';
import {
  ApiRequestError,
  executeSdkRawRequest,
} from './api-request.util';
import { mediaApi } from './sdk-client';

export type PresignUploadInput = {
  mediaType: PresignRequestDtoMediaTypeEnum;
  mimeType: string;
  fileSizeBytes: number;
  fileName?: string;
};

export type PresignUploadResponse = {
  id: string;
  uploadUrl: string;
  headers: Record<string, string>;
};

export type CompleteUploadResponse = {
  id: string;
  url: string;
};

export class MediaError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'MediaError');
  }
}

export async function presignUpload(
  input: PresignUploadInput,
): Promise<PresignUploadResponse> {
  const data = await executeSdkRawRequest<PresignResponseDto>({
    request: () =>
      mediaApi.mediaControllerPresignV1Raw({
        presignRequestDto: {
          mediaType: input.mediaType,
          mimeType: input.mimeType,
          fileSizeBytes: input.fileSizeBytes,
          ...(input.fileName !== undefined
            ? { originalFileName: input.fileName }
            : {}),
        },
      }),
    fallback: 'Failed to prepare profile photo upload',
    ErrorClass: MediaError,
  });

  return {
    id: data.id,
    uploadUrl: data.uploadUrl,
    headers: (data.headers as Record<string, string>) ?? {},
  };
}

export async function completeUpload(mediaId: string): Promise<CompleteUploadResponse> {
  const data = await executeSdkRawRequest<CompleteUploadResponseDto>({
    request: () =>
      mediaApi.mediaControllerCompleteV1Raw({
        id: mediaId,
        completeUploadDto: {},
      }),
    fallback: 'Failed to complete profile photo upload',
    ErrorClass: MediaError,
  });

  return {
    id: data.id,
    url: data.url,
  };
}

export async function uploadProfilePhotoBytes(
  presign: PresignUploadResponse,
  bytes: ArrayBuffer | Blob,
  contentType: string,
): Promise<void> {
  const headers = {
    ...presign.headers,
    'Content-Type': contentType,
  };

  const response = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers,
    body: bytes,
  });

  if (!response.ok) {
    throw new MediaError(response.status, 'Failed to upload profile photo bytes');
  }
}
